import React, { useState } from 'react';
import CommonHeader from '../../util_components/common_header';
import CustomSelect from '../../util_components/custom_single_select';
import CustomInput from '../../util_components/custom_input';
import Button from '../../util_components/button';
import { post_api } from '../../../redux/api_funcs';
import { get_api_url } from '../../../utils/urls';

const CURRENCY_OPTIONS = [
  { value: 'US', label: 'United States (USD)' },
  { value: 'CA', label: 'Canada (CAD)' },
  { value: 'AU', label: 'Australia (AUD)' },
  { value: 'GB', label: 'United Kingdom (GBP)' },
  { value: 'AT', label: 'Eurozone (EUR)' },
  { value: 'AE', label: 'United Arab Emirates (AED)' },
  { value: 'MY', label: 'Malaysia (MYR)' },
  { value: 'SA', label: 'Saudi Arabia (SAR)' },
  { value: 'ZA', label: 'South Africa (ZAR)' },
  { value: 'SG', label: 'Singapore (SGD)' },
  { value: 'NZ', label: 'New Zealand (NZD)' }
];

const SESSIONS_PER_WEEK_OPTIONS = [
  { value: 0, label: '0 Sessions/Week' },
  { value: 1, label: '1 Session/Week' },
  { value: 2, label: '2 Sessions/Week' },
  { value: 3, label: '3 Sessions/Week' },
  { value: 4, label: '4 Sessions/Week' },
  { value: 5, label: '5 Sessions/Week' }
];

function PricingLinks() {
  const [currency, setCurrency] = useState('');
  const [sessionsPerWeek, setSessionsPerWeek] = useState('');
  const [email, setEmail] = useState('');
  const [genericLink, setGenericLink] = useState('');
  const [magicLink, setMagicLink] = useState('');
  const [loading, setLoading] = useState(false);
  const [magicLinkExpiry, setMagicLinkExpiry] = useState('');
  const [copySuccess, setCopySuccess] = useState({ generic: false, magic: false });

  const handleGenerateGenericLink = () => {
    if (currency === '' || sessionsPerWeek === '') {
      alert('Please select both currency and sessions per week');
      return;
    }

    const queryParams = new URLSearchParams({
      country_code: currency,
      s_per_week: sessionsPerWeek
    });

    const STUDENT_BASE =
      import.meta.env.VITE_APP_STAGE === 'production'
        ? 'https://myyogateacher.com'
        : 'https://student-testenv2.myyogateacher.com';
    const link = `${STUDENT_BASE}/pricing?${queryParams.toString()}`;
    setGenericLink(link);
    setMagicLink('');
    setMagicLinkExpiry('');
  };

  const handleGenerateMagicLink = async () => {
    if (!sessionsPerWeek || !email) {
      alert('Please fill in all fields (sessions per week and email)');
      return;
    }

    setLoading(true);
    try {
      const queryParams = new URLSearchParams({
        s_per_week: sessionsPerWeek
      });

      const payload = {
        email: email.trim(),
        redirect: `/pricing?${queryParams.toString()}`,
        is_short_link: true,
        allow_reattempt: 1,
        expiry_in_min: 1440
      };

      const url = get_api_url('/v2/magic_link/get_magic_link_with_expiry', false, true);
      const response = await post_api(url, payload);

      if (response.status === 201) {
        setMagicLink(response.data.data.MagicLink);
        setMagicLinkExpiry(response.data.data.MagicLinkExpiry);
        setGenericLink('');
      } else {
        alert('Failed to generate magic link. Please try again.');
      }
    } catch (error) {
      console.error('Error generating magic link:', error);
      alert('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLink = async (type, link) => {
    try {
      await navigator.clipboard.writeText(link);
      setCopySuccess((prev) => ({ ...prev, [type]: true }));
      setTimeout(() => {
        setCopySuccess((prev) => ({ ...prev, [type]: false }));
      }, 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
      alert('Failed to copy to clipboard');
    }
  };

  const renderLinkOutput = (link, type) => {
    if (!link) return null;

    return (
      <div className="mt-4">
        <div className="p-4 bg-gray-50 rounded-md">
          <p className="text-sm font-medium mb-2">Generated Link:</p>
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={link}
              readOnly
              className="flex-1 p-2 border rounded bg-white"
              onClick={(e) => e.target.select()}
            />
            <Button
              onClick={() => handleCopyLink(type, link)}
              custom_style={{
                backgroundColor: copySuccess[type] ? '#4CAF50' : undefined,
                minWidth: '100px'
              }}
            >
              {copySuccess[type] ? 'Copied!' : 'Copy'}
            </Button>
          </div>
          {type === 'magic' && magicLinkExpiry && (
            <p className="text-sm text-gray-600 mt-2">Magic Link Expiry: {magicLinkExpiry}</p>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="p-4">
      <CommonHeader title="Pricing Links" />

      <div className="mt-8 max-w-2xl mx-auto">
        {/* Generic Link Section */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-lg font-semibold mb-4">Generate Generic Link</h2>
          <div className="mb-6">
            <CustomSelect
              label="Currency"
              options={CURRENCY_OPTIONS}
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
              style={{ width: '100%' }}
            />
          </div>

          <div className="mb-6">
            <CustomSelect
              label="Sessions Per Week"
              options={SESSIONS_PER_WEEK_OPTIONS}
              value={sessionsPerWeek}
              onChange={(e) => setSessionsPerWeek(e.target.value)}
              style={{ width: '100%' }}
            />
          </div>

          <div className="flex justify-center">
            <Button
              onClick={handleGenerateGenericLink}
              disabled={currency === '' || sessionsPerWeek === '' || loading}
            >
              Generate Generic Link
            </Button>
          </div>

          {renderLinkOutput(genericLink, 'generic')}
        </div>

        {/* Magic Link Section */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4">Generate Magic Link</h2>
          <div className="mb-6">
            <CustomSelect
              label="Sessions Per Week"
              options={SESSIONS_PER_WEEK_OPTIONS}
              value={sessionsPerWeek}
              onChange={(e) => setSessionsPerWeek(e.target.value)}
              style={{ width: '100%' }}
            />
          </div>

          <div className="mb-6">
            <CustomInput
              label="Email"
              placeholder="Enter email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={{ width: '100%' }}
            />
          </div>

          <div className="flex justify-center">
            <Button
              onClick={handleGenerateMagicLink}
              disabled={!sessionsPerWeek || !email || loading}
            >
              Generate Magic Link
            </Button>
          </div>

          {renderLinkOutput(magicLink, 'magic')}
        </div>
      </div>
    </div>
  );
}

export default PricingLinks;
